import React, { useCallback, useEffect, useState } from "react";
import { Monitor } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import SubscriberAvatar from "@/components/SubscriberAvatar";
import { openKiosk } from "@/lib/openKiosk";
import { ATTENDANCE_REFRESH_KEY } from "@/lib/attendanceSync";
import {
  useSettings,
  isAttendanceAvailable,
  resolveGymTimezone,
  gymTodayIsoDate,
  formatInGymTimezone,
} from "@/lib/utils-app";

export default function AdminAttendance() {
  const settings = useSettings();
  const gymTz = resolveGymTimezone(settings);
  const todayGym = gymTodayIsoDate(gymTz);

  const [items, setItems] = useState([]);
  const [date, setDate] = useState(todayGym);
  const [loading, setLoading] = useState(false);
  const [openingKiosk, setOpeningKiosk] = useState(false);
  const attendanceOn = isAttendanceAvailable(settings);

  const handleOpenKiosk = () => {
    setOpeningKiosk(true);
    openKiosk();
    toast.success("Kiosk abierto en nueva pestaña");
    setOpeningKiosk(false);
  };

  useEffect(() => {
    setDate(gymTodayIsoDate(gymTz));
  }, [gymTz]);

  const loadItems = useCallback(() => {
    if (!date) return;
    setLoading(true);
    api
      .get("/admin/attendance", { params: { date } })
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [date]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === ATTENDANCE_REFRESH_KEY) loadItems();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") loadItems();
    };
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [loadItems]);

  const fmtTime = (iso) =>
    iso
      ? formatInGymTimezone(iso, gymTz, {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        })
      : "—";

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#E63946]">Operación</div>
          <h1 className="font-display text-4xl text-white mt-1">Registro de asistencia</h1>
        </div>
        {attendanceOn && (
          <button
            type="button"
            onClick={handleOpenKiosk}
            disabled={openingKiosk}
            className="btn-primary bg-green-700 hover:bg-green-600 text-sm py-2 shrink-0"
            data-testid="open-kiosk-btn"
          >
            <Monitor size={16} />
            {openingKiosk ? "Abriendo…" : "Abrir kiosk de asistencia"}
          </button>
        )}
      </div>
      <div className="mt-4 flex flex-wrap items-end gap-4">
        <div>
          <label className="text-xs uppercase tracking-widest text-white/60">Fecha (horario del gimnasio)</label>
          <input
            type="date"
            className="input-dark mt-1 max-w-xs block"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <button
          type="button"
          className="btn-outline text-xs py-2"
          onClick={() => setDate(todayGym)}
        >
          Hoy ({todayGym})
        </button>
        <span className="text-xs text-white/40 pb-2">
          {loading ? "Cargando…" : `${items.length} atleta${items.length === 1 ? "" : "s"}`}
        </span>
      </div>
      <p className="text-xs text-white/40 mt-2">
        Un escaneo registra la asistencia del día. Llegada antes de 15 min del turno: se registra el intento pero no cuenta como asistencia.
      </p>
      <div className="mt-6 glass rounded-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[520px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-white/40 border-b border-theme-secondary/10">
              <th className="p-3">Asistencia</th>
              <th className="p-3">Atleta</th>
              <th className="p-3">Turno</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-b border-theme-secondary/10 text-white/60">
                <td className="p-3 tabular-nums">{fmtTime(a.attendance_at || a.check_in_at)}</td>
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <SubscriberAvatar
                      subscriber={{ profile_photo: a.profile_photo, has_profile_photo: !!a.profile_photo }}
                      size={40}
                      className="border border-white/15"
                    />
                    <span className="text-white">{a.subscriber_name}</span>
                  </div>
                </td>
                <td className="p-3">{a.shift_name || "—"}</td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={3} className="text-center py-10 text-white/40">
                  Sin asistencias en esta fecha.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
